// Old tabs send unscoped END messages. Do not mix the two lifecycle protocols.
export const PLANK_TO_MWD_DRAG_CHANNEL = 'bam-plank-to-mwd-dnd-v2';
export const PLANK_TO_MWD_DRAG_DATA_TYPE = 'application/x-bam-plank-to-mwd-drag';
export const PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS = 30_000;

export type PlankToMwdDragKind = 'defect' | 'conflict' | 'reserve';

export type PlankToMwdDragItem = {
  kind: PlankToMwdDragKind;
  sourceId: string;
  label: string;
  werklijnId?: string;
  eenheidNummer?: string;
  materieelSoort?: string;
  /** The specific deeltraject (inzet/claim) this item refers to, e.g. a conflict's own
   * werklijnElementId. Lets MWD open the exact day/inzet the item was dragged from instead of
   * defaulting to the werklijn's first inzet, which may fall on a different day. */
  werklijnElementId?: string;
};

export type PlankToMwdDragSession = {
  sessionId: string;
  timestamp: number;
};

export type PlankToMwdDragPayload = PlankToMwdDragItem &
  PlankToMwdDragSession & {
    /** Every dragged item in selection order, including this payload's own. Absent for a
     * single-item drag. */
    items?: PlankToMwdDragItem[];
  };

export type PlankToMwdDragMessage =
  | {
      type: 'PLANK_MWD_DRAG_START';
      payload: PlankToMwdDragPayload;
    }
  | (PlankToMwdDragSession & {
      type: 'PLANK_MWD_DRAG_END';
    })
  | {
      type: 'PLANK_MWD_DROP_CONSUMED';
      payload: PlankToMwdDragPayload;
    };

/** Channel linking the Plank and MWD windows; they are sibling routes and never share a
 * document. Null when BroadcastChannel is unsupported.
 */
export function createPlankToMwdDragChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(PLANK_TO_MWD_DRAG_CHANNEL);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isDragSession(value: unknown): value is PlankToMwdDragSession {
  return (
    isRecord(value) &&
    typeof value.sessionId === 'string' &&
    value.sessionId.length > 0 &&
    typeof value.timestamp === 'number' &&
    Number.isFinite(value.timestamp)
  );
}

function isDragItem(value: unknown): value is PlankToMwdDragItem {
  return (
    isRecord(value) &&
    ['defect', 'conflict', 'reserve'].includes(value.kind as string) &&
    typeof value.sourceId === 'string' &&
    typeof value.label === 'string' &&
    ['werklijnId', 'eenheidNummer', 'materieelSoort', 'werklijnElementId'].every(
      (key) => value[key] === undefined || typeof value[key] === 'string',
    )
  );
}

export function isPlankToMwdDragPayload(value: unknown): value is PlankToMwdDragPayload {
  return (
    isDragSession(value) &&
    isDragItem(value) &&
    (!('items' in value) || value.items === undefined || (Array.isArray(value.items) && value.items.every(isDragItem)))
  );
}

export function isPlankToMwdDragMessage(value: unknown): value is PlankToMwdDragMessage {
  if (!isRecord(value)) return false;
  if (value.type === 'PLANK_MWD_DRAG_END') return isDragSession(value);
  return (
    (value.type === 'PLANK_MWD_DRAG_START' || value.type === 'PLANK_MWD_DROP_CONSUMED') &&
    isPlankToMwdDragPayload(value.payload)
  );
}

/** True while the payload is younger than PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS. */
export function isFreshPlankToMwdDragPayload(payload: PlankToMwdDragSession, now = Date.now()) {
  const age = now - payload.timestamp;
  return age >= 0 && age < PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS;
}

/** Every dragged item: the batch items when present, otherwise the single top-level item. */
export function getPlankToMwdDragItems(payload: PlankToMwdDragPayload): PlankToMwdDragItem[] {
  return payload.items && payload.items.length > 0 ? payload.items : [payload];
}

/** True when at least one item carries a werklijnId or eenheidNummer to resolve. */
export function isResolvablePlankToMwdDragPayload(payload: PlankToMwdDragPayload) {
  return getPlankToMwdDragItems(payload).some((item) => item.werklijnId || item.eenheidNummer);
}

/** Null for absent/unavailable transport; throws for malformed or legacy payloads. */
export function readPlankToMwdDragPayload(dataTransfer: DataTransfer): PlankToMwdDragPayload | null {
  let serializedPayload: string;
  try {
    serializedPayload = dataTransfer.getData(PLANK_TO_MWD_DRAG_DATA_TYPE);
  } catch {
    return null;
  }
  if (!serializedPayload) return null;
  const payload: unknown = JSON.parse(serializedPayload);
  if (!isPlankToMwdDragPayload(payload)) throw new Error('Invalid Plank drag payload');
  return payload;
}
